import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { NgClass } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { SidebarService } from '@shared/services/sidebar.service';
import { ThemeToggleButtonComponent } from '@shared/components/common/theme-toggle/theme-toggle-button.component';
import { UserDropdownComponent } from '@shared/components/header/user-dropdown/user-dropdown.component';
import { SelectSearchComponent } from '@shared/components/form/select-search/select-search.component';
import { NotificationIconComponent } from '@pages/modulos/control-general/notificaciones/notification-dropdown/notification-icon.component';
import { EmpresaService } from '@pages/empresas/services/empresa.service';
import { CompanyContextService } from '@pages/empresas/services/company-context.service';
import { AuthService } from '@shared/services/auth.service';
import { UsuarioService } from '@shared/services/usuario/usuario.service';
import { PermissionService } from '@shared/services/permission/permission.service';
import { EmpresaDTO } from '@pages/empresas/models/empresa.model';

const DEFAULT_LOGO = 'assets/images/Partner-Core-No-Image.webp';

@Component({
  selector: 'app-header',
  imports: [
    NgClass,
    FormsModule,
    RouterModule,
    ThemeToggleButtonComponent,
    NotificationIconComponent,
    UserDropdownComponent,
    SelectSearchComponent,
  ],
  templateUrl: './app-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppHeaderComponent {
  private readonly sidebarService = inject(SidebarService);
  private readonly empresaService = inject(EmpresaService);
  private readonly authService = inject(AuthService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly usuarioService = inject(UsuarioService);
  private readonly permissionService = inject(PermissionService);

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  // Sidebar state as signals — subscription lifecycle managed automatically
  readonly isMobileOpen = toSignal(this.sidebarService.isMobileOpen$, { initialValue: false });
  readonly isExpanded = toSignal(this.sidebarService.isExpanded$, { initialValue: false });

  readonly isApplicationMenuOpen = computed(() => this.sidebarService.headerMenuOpen());

  // Company selector options — derived from the empresa list signal
  readonly companyOptions = computed(() =>
    this.empresaService.empresas().map(e => ({ value: String(e.id), label: e.companyName }))
  );

  // Selected company — derived from context (single source of truth)
  readonly selectedCompany = computed<string | null>(() => {
    const id = this.companyContext.companyId();
    return id !== null ? String(id) : null;
  });

  /**
   * Base de la ruta de Empresas accesible para el usuario (Sistema vs Control General), resuelta
   * desde sus permisos de módulo. Null si no tiene acceso a Empresas (el logo no navega entonces).
   */
  private readonly companyDetailBase = computed<string | null>(() => {
    const roleId = this.usuarioService.perfil()?.role?.id;
    if (!roleId) return null;
    if (this.permissionService.checkModulePermission(roleId, '/admin-sistema/empresas', 'view')) return '/admin-sistema/empresas';
    if (this.permissionService.checkModulePermission(roleId, '/admin/empresas', 'view')) return '/admin/empresas';
    return null;
  });

  /**
   * RouterLink al detalle de la empresa seleccionada (`<base>/<companyId>`), o null si no hay empresa
   * activa o el usuario no puede ver Empresas. La ruta destino está protegida por `permissionGuard`.
   */
  readonly companyDetailLink = computed<(string | number)[] | null>(() => {
    const base = this.companyDetailBase();
    const id = this.companyContext.companyId();
    return base && id !== null ? [base, id] : null;
  });

  // Company logo — writable signal updated by the logo effect below
  readonly imgLogo = signal(DEFAULT_LOGO);

  // Current company — reacts to both selection changes and direct data updates
  private readonly currentCompany = computed<EmpresaDTO | null>(() => {
    const id = this.companyContext.companyId();
    const updated = this.companyContext.onCompanyUpdated()();
    if (updated && (updated as EmpresaDTO).id === id) {
      return updated as EmpresaDTO;
    }
    return this.empresaService.empresas().find(e => e.id === id) ?? null;
  });

  constructor() {
    // Valida la seleccion persistida cuando carga la lista.
    // Si el ID guardado ya no existe (empresa eliminada) o no hay seleccion, usa la primera empresa.
    effect(() => {
      const list = this.empresaService.empresas();
      if (list.length === 0) return;

      const storedId = this.companyContext.companyId();
      const isValid = storedId !== null && list.some(e => e.id === storedId);

      if (!isValid) {
        this.companyContext.setCompany(list[0].id);
      }
    });

    // Load and update logo reactively
    // onCleanup cancels any in-flight request and revokes the blob URL on each re-run and on destroy
    effect((onCleanup) => {
      const company = this.currentCompany();

      if (!company?.logoUrl) {
        this.imgLogo.set(DEFAULT_LOGO);
        return;
      }

      const parts = company.logoUrl.split('/');
      const filename = parts.at(-1);
      const companyId = Number(parts.at(-2));

      if (!filename || !companyId) {
        this.imgLogo.set(DEFAULT_LOGO);
        return;
      }

      let blobUrl: string | null = null;

      const sub = this.empresaService.getCompanyLogoBlob(companyId, filename).subscribe({
        next: blob => {
          blobUrl = URL.createObjectURL(blob);
          this.imgLogo.set(blobUrl);
        },
        error: () => this.imgLogo.set(DEFAULT_LOGO)
      });

      onCleanup(() => {
        sub.unsubscribe();
        if (blobUrl) URL.revokeObjectURL(blobUrl);
      });
    });
  }

  ngOnInit(): void {
    if (this.authService.isLoggedIn()) {
      this.empresaService.loadCompaniesByClient().subscribe();
    }
  }

  onCompanySelected(id: string): void {
    this.companyContext.setCompany(Number(id));

    // Limpiar caches del rol anterior para forzar recarga con el rol de la nueva empresa
    this.permissionService.clearSidebarCache();

    // Recargar el perfil: el interceptor X-Company-Id enviará el nuevo companyId
    // y el backend devolverá el rol de la membresía en esa empresa.
    // El sidebar se recarga automáticamente vía el effect en AppSidebarMenuComponent
    // al detectar el cambio en perfil() y companyId().
    this.usuarioService.getPerfil(true).subscribe();
  }

  /**
   * Listener de resize para sincronizar breakpoint con el servicio
   */
  @HostListener('window:resize', ['$event'])
  onWindowResize(event: Event) {
    const currentWidth = (event.target as Window).innerWidth;
    this.sidebarService.updateBreakpoint(currentWidth);
  }

  /**
   * Maneja el toggle del sidebar según el breakpoint actual
   * Usa el breakpoint del servicio para garantizar consistencia
   */
  handleToggle() {
    const breakpoint = this.sidebarService.getCurrentBreakpoint();
    
    if (breakpoint === 'desktop') {
      this.sidebarService.toggleExpanded();
    } else {
      this.sidebarService.toggleMobileOpen();
    }
  }

  /**
   * Alterna la visibilidad del menú de aplicaciones (tema, notificaciones, usuario)
   * Notifica al servicio para que el sidebar se ajuste dinámicamente
   */
  toggleApplicationMenu() {
    this.sidebarService.toggleHeaderMenu();
  }

  ngAfterViewInit() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  ngOnDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.searchInput?.nativeElement.focus();
    }
  };
}